import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Phone, ArrowLeft, MoreVertical, User, Bell, BellOff } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getAvatarColor } from '@/lib/utils';

interface InboxHeaderProps {
  contactName: string;
  contactPhone?: string;
  avatarUrl?: string;
  onCall?: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  onClickContact?: () => void;
}

export function InboxHeader({
  contactName,
  contactPhone,
  onCall,
  onBack,
  showBackButton,
  onClickContact,
}: InboxHeaderProps) {
  const [muted, setMuted] = useState(false);

  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      toast.success(next ? 'Notificações silenciadas' : 'Notificações reativadas');
      return next;
    });
  };

  return (
    <div className="px-4 py-2.5 bg-wa-header border-b border-wa-divider rounded-t-2xl flex-shrink-0">
      <div className="flex items-center gap-3">
        {showBackButton && (
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-wa-text-muted hover:bg-wa-sidebar-active rounded-full h-9 w-9"
            onClick={onBack}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <Avatar
          className="h-10 w-10 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={onClickContact}
        >
          <AvatarFallback className={`bg-gradient-to-br ${getAvatarColor(contactName)} text-white`}>
            {contactName?.charAt(0) || '?'}
          </AvatarFallback>
        </Avatar>
        <div
          className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={onClickContact}
        >
          <h2 className="font-medium text-wa-text text-[15px] truncate leading-tight">
            {contactName || 'Sem nome'}
          </h2>
          <p className="text-[12px] text-wa-text-muted truncate leading-tight">{contactPhone}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-wa-text-muted hover:bg-wa-sidebar-active rounded-full h-9 w-9"
          onClick={onCall}
          disabled={!contactPhone}
          title="Ligar"
        >
          <Phone className="h-[18px] w-[18px]" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-wa-text-muted hover:bg-wa-sidebar-active rounded-full h-9 w-9 hidden sm:flex"
              title="Mais opções"
            >
              <MoreVertical className="h-[18px] w-[18px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={onClickContact} disabled={!onClickContact}>
              <User className="h-4 w-4" />
              Ver dados do contato
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleMute}>
              {muted ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              {muted ? 'Reativar notificações' : 'Silenciar notificações'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
